import { assertIsDefined, isEmpty, isNotDefined } from '@sgme/fp';
import * as Papa from 'papaparse';

import { cleanupNumberWith } from '@/App/tiles/shared/import/numbers';
import type { TradeTypeExample } from '@/App/tiles/shared/import/typings.ts';
import type { TargetAccuType } from '@/store/targetAccumulator/state.ts';
import { type DateInputCultureInfo, parseWithCultureInfo } from '@/utils/parseDateWithCultureInfo.ts';

type CommonError = 'INVALID_FILE' | 'MISSING_COLUMN';

export interface CsvValidationFailure<ERR extends string> {
  error: ERR | CommonError;
  faultyLines: readonly number[];
}

interface CsvValidationSuccess<T> {
  rows: readonly T[];
}

export type CsvValidationResult<T, ERR extends string> = CsvValidationFailure<ERR> | CsvValidationSuccess<T>;

//Temporary remove when Forward Accu is implemented
export const ForwardAccuTypes = ['Standard', 'Permanent'] as const;
export type ForwardAccuType = (typeof ForwardAccuTypes)[number];

export type AccumulatorType = TargetAccuType | ForwardAccuType;

export interface Cleaners {
  cleanupNumber(value: string, isNotional?: boolean): string;
  sanitizeDate(value: string): string;
  accuType?: AccumulatorType;
}

type RowMapper<T> = (cleaners: Cleaners) => (parsedRow: any[]) => T;

export const processFileWith =
  <T, ERR extends string>(requiredColumns: string[], validators: Record<ERR, RowPredicate>, mapRowWith: RowMapper<T>) =>
  (file: File, dateInputCultureInfo: DateInputCultureInfo) =>
    new Promise<CsvValidationResult<T, ERR>>((resolve, reject) => {
      const lcFilename = file.name.toLowerCase();
      if (!lcFilename.endsWith('.csv') && !lcFilename.endsWith('.tsv')) {
        resolve({ error: 'INVALID_FILE', faultyLines: [] });
        return;
      }
      Papa.parse<T[], File>(file, {
        skipEmptyLines: true,
        complete: ({ data, meta: { delimiter } }) => {
          const validateImport = validateImportWith(
            requiredColumns.map((s) => s.toLowerCase()),
            validators,
          );

          const [firstValidationError] = validateImport(data);

          if (firstValidationError === undefined) {
            const cleanupNumber = cleanupNumberWith(delimiter);
            const sanitizeDate = parseWithCultureInfo.bind(undefined, dateInputCultureInfo);

            const mapRow = mapRowWith({
              cleanupNumber,
              sanitizeDate,
            });

            resolve({ rows: data.slice(1).map(mapRow) });
          } else {
            resolve(firstValidationError);
          }
        },
        error: reject,
      });
    });

export type RowPredicate = (
  row: ReadonlyArray<string | undefined>,
  rows: ReadonlyArray<ReadonlyArray<string | undefined>>,
) => boolean;

const mapFaultyLinesValidator =
  (validator: (data: readonly any[]) => readonly number[]) =>
  (resultData: readonly any[]): [boolean, readonly number[]] => {
    const faultyLines = validator(resultData);
    return [isEmpty(faultyLines), faultyLines];
  };

export const isValidValue = (validValues: readonly string[], value: string | undefined): boolean =>
  value !== undefined && validValues.includes(value.toLowerCase());

export const validateImportWith =
  <T extends string>(requiredColumns: readonly string[], linesValidators: Record<T, RowPredicate>) =>
  (
    data: any[],
  ): ReadonlyArray<{
    error: T | CommonError;
    faultyLines: readonly number[];
  }> =>
    allValidators<T>(requiredColumns, linesValidators)
      .map(([potentialError, validator]) => [potentialError, validator(data)] as const)
      .filter(([, [isValid]]) => !isValid)
      .map(([error, [, faultyLines]]) => ({ error, faultyLines }));

type ValidationTuple<T extends string> = readonly [
  T | CommonError,
  (resultData: readonly any[]) => [boolean, readonly number[]],
];

const allValidators = <T extends string>(
  requiredColumns: readonly string[],
  linesValidators: Record<T, RowPredicate>,
): Array<ValidationTuple<T>> => [
  ['INVALID_FILE', (lines: readonly any[]) => [lines.length >= 2, []]],
  ['MISSING_COLUMN', mapFaultyLinesValidator(validateColumns(requiredColumns))],
  ...Object.entries<RowPredicate>(linesValidators).map(
    ([potentialError, validator]) => [potentialError as T, mapFaultyLinesValidator(validateLines(validator))] as const,
  ),
];

/**
 * Validate that all columns are present and in the correct order.
 * Also checks that all lines have at least as many values as `requiredColumns`
 * @param requiredColumns Array of required columns in the expected order
 * @returns The array of lines indices where there are missing values
 */
export const validateColumns =
  (requiredColumns: readonly string[]) =>
  (resultData: readonly any[][]): readonly number[] => {
    const headerRow = resultData[0];
    assertIsDefined(headerRow, 'resultData is unexpectedly empty, cannot read the header row');

    const headers = headerRow.map((h: string) => h.trim().toLowerCase());

    if (
      headers.length < requiredColumns.length ||
      requiredColumns.some((c, i) => c !== headers[i]) // Check that the columns are present and in the right order
    ) {
      return [0];
    }

    return validateLines((row) => row.length >= requiredColumns.length)(resultData);
  };

export const validateLines =
  (rowPredicate: (row: readonly any[], rows: readonly any[][]) => boolean) =>
  (resultData: readonly any[]): readonly number[] => {
    const headerLessData = resultData.slice(1);
    return headerLessData
      .map((row, index) => [row, index] as const)
      .filter(([row]) => !rowPredicate(row, headerLessData))
      .map(([, line]) => line + 1);
  };

export const liftToOptionalString =
  <T extends unknown[]>(transform: (v: string, ...r: T) => string, ...rest: T) =>
  (value: string | undefined): string | undefined => {
    const isEmptyOrNotDefined = isEmpty(value) || isNotDefined(value);
    return isEmptyOrNotDefined ? undefined : transform(value, ...rest);
  };

export const getTradeExamples = (tradeType: TradeTypeExample | TargetAccuType, accuType?: AccumulatorType) => {
  if (tradeType !== 'TargetAccumulator') {
    return tradeType;
  }
  return accuType as TargetAccuType;
};
